import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { useCreateClass, useInstitutionClasses } from '../hooks/useInstitution'
import { ClassAssignForm } from './ClassAssignForm'

export function ClassManager() {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [level, setLevel] = useState('')
  const { data: classes, isLoading, isError } = useInstitutionClasses()
  const createClass = useCreateClass()

  function handleSubmit(event: FormEvent) {
    event.preventDefault()

    createClass.mutate(
      { name: name.trim(), level: level.trim() === '' ? undefined : level.trim() },
      { onSuccess: () => { setName(''); setLevel('') } }
    )
  }

  return (
    <section aria-labelledby="classes-title" className="flex flex-col gap-4">
      <h2 id="classes-title" className="font-display text-lg font-semibold text-on-surface">
        {t('institution.classes.title')}
      </h2>

      <form onSubmit={handleSubmit} className="flex flex-col gap-3 md:flex-row md:items-end">
        <div className="flex-1">
          <Input
            id="institution-class-name"
            label={t('institution.classes.name')}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <div className="flex-1">
          <Input
            id="institution-class-level"
            label={t('institution.classes.level')}
            value={level}
            onChange={(event) => setLevel(event.target.value)}
          />
        </div>
        <Button type="submit" disabled={name.trim() === ''} isLoading={createClass.isPending}>
          {t('institution.classes.create')}
        </Button>
      </form>

      {isError && <Alert variant="error">{t('institution.classes.load_error')}</Alert>}
      {isLoading && <div className="h-24 animate-pulse rounded-2xl bg-surface-container" />}

      {classes && classes.length === 0 && (
        <p className="rounded-2xl bg-surface-low px-4 py-6 text-center text-sm text-on-surface-variant">
          {t('institution.classes.empty')}
        </p>
      )}

      {classes && classes.length > 0 && (
        <ul className="flex flex-col gap-3">
          {classes.map((institutionClass) => (
            <li
              key={institutionClass.id}
              className="flex flex-col gap-3 rounded-2xl bg-surface-low px-4 py-3"
            >
              <span className="flex items-baseline justify-between gap-3">
                <span className="font-semibold text-on-surface">{institutionClass.name}</span>
                <span className="text-xs text-on-surface-variant">
                  {t('institution.classes.students', { count: institutionClass.students_count })}
                </span>
              </span>

              <ClassAssignForm classId={institutionClass.id} className={institutionClass.name} />
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
